import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, Loader2, AlertCircle, Calculator, ArrowRight, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingProcedures = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('pricing');
  const [viewItem, setViewItem] = useState(null);
  const [pricingProcedures, setPricingProcedures] = useState([]);
  const [conditionTypes, setConditionTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState(null);
  const [expandedProcedure, setExpandedProcedure] = useState(null);
  const [formData, setFormData] = useState({
    pricing_procedure_id: '',
    description: '',
    steps: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [procRes, typesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pricing/pricing-procedures`),
        axios.get(`${BACKEND_URL}/api/pricing/condition-types`)
      ]);
      setPricingProcedures(procRes.data || []);
      setConditionTypes(typesRes.data || []);
    } catch (error) {
      console.error('Error loading pricing procedures:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load pricing procedures: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (proc = null) => {
    if (proc) {
      setEditingProcedure(proc);
      // Sort steps by step_number when loading
      const sortedSteps = [...(proc.steps || [])].sort((a, b) => a.step_number - b.step_number);
      setFormData({
        pricing_procedure_id: proc.pricing_procedure_id,
        description: proc.description,
        steps: sortedSteps
      });
    } else {
      setEditingProcedure(null);
      setFormData({
        pricing_procedure_id: '',
        description: '',
        steps: [createNewStep(10)]
      });
    }
    setIsDialogOpen(true);
  };

  const createNewStep = (stepNumber) => ({
    step_number: stepNumber,
    counter: 0,
    condition_type_id: '',
    description: '',
    from_step: null,
    to_step: null,
    is_manual: false,
    is_mandatory: false,
    is_statistical: false,
    subtotal_type: null,
    requirement: null,
    alt_calc_type: null,
    alt_base_value: null,
    account_key: null
  });

  const handleAddStep = () => {
    const lastStep = formData.steps[formData.steps.length - 1];
    const newStepNumber = lastStep ? lastStep.step_number + 10 : 10;
    setFormData({
      ...formData,
      steps: [...formData.steps, createNewStep(newStepNumber)]
    });
  };

  const handleRemoveStep = (index) => {
    if (formData.steps.length <= 1) {
      toast.error('Pricing procedure must have at least one step');
      return;
    }
    const newSteps = formData.steps.filter((_, i) => i !== index);
    setFormData({ ...formData, steps: newSteps });
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    
    // Auto-fill description from condition type if empty
    if (field === 'condition_type_id' && value) {
      const ct = conditionTypes.find(c => c.condition_type_id === value);
      if (ct && !newSteps[index].description) {
        newSteps[index].description = ct.description;
      }
    }
    
    setFormData({ ...formData, steps: newSteps });
  };

  const moveStep = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === formData.steps.length - 1)) {
      return;
    }
    const newSteps = [...formData.steps];
    const temp = newSteps[index];
    newSteps[index] = newSteps[index + direction];
    newSteps[index + direction] = temp;
    
    // Update step numbers
    newSteps.forEach((step, i) => {
      step.step_number = (i + 1) * 10;
    });
    
    setFormData({ ...formData, steps: newSteps });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.pricing_procedure_id || formData.pricing_procedure_id.trim().length === 0) {
      toast.error('Pricing Procedure ID is required');
      return;
    }
    if (formData.pricing_procedure_id.length > 10) {
      toast.error('Pricing Procedure ID must be max 10 characters');
      return;
    }
    if (!formData.description || formData.description.trim().length === 0) {
      toast.error('Description is required');
      return;
    }
    if (formData.steps.length === 0) {
      toast.error('At least one step is required');
      return;
    }
    
    // Validate each step
    for (let i = 0; i < formData.steps.length; i++) {
      const step = formData.steps[i];
      if (step.step_number < 1) {
        toast.error(`Step ${i + 1}: Step number must be a positive value`);
        return;
      }
      if (!step.description) {
        toast.error(`Step ${i + 1}: Description is required`);
        return;
      }
    }

    setIsSaving(true);
    try {
      // Sort steps by step_number before saving
      const sortedSteps = [...formData.steps].sort((a, b) => a.step_number - b.step_number);
      const data = {
        pricing_procedure_id: formData.pricing_procedure_id.toUpperCase().trim(),
        description: formData.description.trim(),
        steps: sortedSteps.map((step, index) => ({
          step_number: step.step_number || (index + 1) * 10,
          counter: step.counter || 0,
          condition_type_id: step.condition_type_id || null,
          description: step.description,
          from_step: step.from_step || null,
          to_step: step.to_step || null,
          is_manual: step.is_manual || false,
          is_mandatory: step.is_mandatory || false,
          is_statistical: step.is_statistical || false,
          subtotal_type: step.subtotal_type || null,
          requirement: step.requirement || null,
          alt_calc_type: step.alt_calc_type || null,
          alt_base_value: step.alt_base_value || null,
          account_key: step.account_key || null
        }))
      };

      if (editingProcedure) {
        await axios.put(`${BACKEND_URL}/api/pricing/pricing-procedures/${editingProcedure.pricing_procedure_id}`, data);
        toast.success(`Pricing Procedure '${editingProcedure.pricing_procedure_id}' updated successfully`);
      } else {
        await axios.post(`${BACKEND_URL}/api/pricing/pricing-procedures`, data);
        toast.success(`Pricing Procedure '${data.pricing_procedure_id}' created successfully`);
      }
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving pricing procedure:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to save pricing procedure: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (procId) => {
    if (!window.confirm(`Are you sure you want to delete Pricing Procedure '${procId}'? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/pricing/pricing-procedures/${procId}`);
      toast.success(`Pricing Procedure '${procId}' deleted successfully`);
      loadData();
    } catch (error) {
      console.error('Error deleting pricing procedure:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to delete pricing procedure: ${errorMsg}`);
    }
  };

  const getConditionType = (typeId) => conditionTypes.find(ct => ct.condition_type_id === typeId);

  const getStepTypeColor = (step) => {
    const ct = getConditionType(step.condition_type_id);
    if (!ct) return 'bg-gray-100';
    switch (ct.condition_category) {
      case 'BASE': return 'bg-orange-100 border-orange-300';
      case 'DISC': return 'bg-green-100 border-green-300';
      case 'SURCH': return 'bg-orange-100 border-orange-300';
      case 'FREIGHT': return 'bg-orange-100 border-orange-300';
      case 'TAX': return 'bg-red-100 border-red-300';
      default: return 'bg-gray-100 border-gray-300';
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pricing Procedures</h1>
            <p className="text-sm text-gray-600 mt-1">Define the calculation schema for pricing (step-by-step)</p>
          </div>
          {writable && (<Button onClick={() => handleOpenDialog()} data-testid="add-pricing-procedure-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Pricing Procedure
          </Button>)}
        </div>

        <Card className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading pricing procedures...</span>
            </div>
          ) : pricingProcedures.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No pricing procedures found.</p>
              <p className="text-sm text-gray-400 mt-1">Click "Add Pricing Procedure" to create one.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pricingProcedures.map((proc) => (
                <div key={proc.pricing_procedure_id} className="border rounded-lg" data-testid={`procedure-${proc.pricing_procedure_id}`}>
                  <div 
                    className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedProcedure(expandedProcedure === proc.pricing_procedure_id ? null : proc.pricing_procedure_id)}
                  >
                    <div className="flex items-center gap-3">
                      <Calculator className="w-5 h-5 text-orange-600" />
                      <div>
                        <h3 className="font-bold text-lg">{proc.pricing_procedure_id}</h3>
                        <p className="text-gray-600 text-sm">{proc.description}</p>
                      </div>
                      <span className="text-sm text-gray-500 ml-4">({(proc.steps || []).length} steps)</span>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <RecordActions
                        doctype="pricing"
                        idForTestId={proc.pricing_procedure_id}
                        onView={() => setViewItem(proc)}
                        onEdit={() => handleOpenDialog(proc)}
                      />
                      {expandedProcedure === proc.pricing_procedure_id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Steps */}
                  {expandedProcedure === proc.pricing_procedure_id && (
                    <div className="border-t px-4 py-3 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-500 mb-3">Calculation Steps:</p>
                      <div className="space-y-2">
                        {[...(proc.steps || [])].sort((a, b) => a.step_number - b.step_number).map((step, index, sortedSteps) => (
                          <div key={`step-${step.step_number}`} className="flex items-center gap-2">
                            <div className={`flex-1 p-3 rounded-lg border ${getStepTypeColor(step)}`}>
                              <div className="flex items-center gap-3">
                                <span className="font-mono font-bold text-sm bg-white px-2 py-1 rounded">{step.step_number}</span>
                                {step.condition_type_id ? (
                                  <span className="font-bold">{step.condition_type_id}</span>
                                ) : (
                                  <span className="font-bold text-gray-500 italic">Subtotal</span>
                                )}
                                <span className="text-gray-600">{step.description}</span>
                                {step.is_statistical && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Statistical</span>}
                                {step.from_step && <span className="text-xs text-gray-500">From: {step.from_step}</span>}
                                {step.to_step && <span className="text-xs text-gray-500">To: {step.to_step}</span>}
                                {step.account_key && <span className="text-xs bg-orange-200 px-2 py-0.5 rounded">Acct: {step.account_key}</span>}
                              </div>
                            </div>
                            {index < sortedSteps.length - 1 && (
                              <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProcedure ? 'Edit Pricing Procedure' : 'Create Pricing Procedure'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pricing_procedure_id">Pricing Procedure ID * (max 10 chars)</Label>
                  <Input
                    id="pricing_procedure_id"
                    value={formData.pricing_procedure_id}
                    onChange={(e) => setFormData({...formData, pricing_procedure_id: e.target.value.toUpperCase()})}
                    placeholder="e.g., ZSTD01, ZSD001"
                    maxLength={10}
                    disabled={!!editingProcedure}
                    className="uppercase"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Standard Sales Pricing Procedure"
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              {/* Steps */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Calculation Steps (in order)</h3>
                  <Button type="button" size="sm" variant="outline" onClick={handleAddStep}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.steps.map((step, index) => (
                    <div key={`form-step-${step.id || step.step_number || index}`} className="bg-white p-4 rounded border">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-1">
                          <Button type="button" size="sm" variant="ghost" onClick={() => moveStep(index, -1)} disabled={index === 0}>
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <GripVertical className="w-4 h-4 text-gray-400 mx-auto" />
                          <Button type="button" size="sm" variant="ghost" onClick={() => moveStep(index, 1)} disabled={index === formData.steps.length - 1}>
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="flex-1 space-y-3">
                          {/* Row 1: Step#, Condition Type, Description */}
                          <div className="grid grid-cols-6 gap-3">
                            <div>
                              <Label className="text-xs">Step # *</Label>
                              <Input
                                type="number"
                                min="1"
                                value={step.step_number}
                                onChange={(e) => handleStepChange(index, 'step_number', Math.max(1, parseInt(e.target.value) || 1))}
                                className="text-center"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Condition Type</Label>
                              <Select 
                                value={step.condition_type_id || '__NONE__'} 
                                onValueChange={(v) => handleStepChange(index, 'condition_type_id', v === '__NONE__' ? '' : v)}
                              >
                                <SelectTrigger><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__NONE__">-- None (Subtotal/Total) --</SelectItem>
                                  {conditionTypes.map(ct => (
                                    <SelectItem key={ct.condition_type_id} value={ct.condition_type_id}>
                                      {ct.condition_type_id} - {ct.description}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs">Description *</Label>
                              <Input
                                value={step.description}
                                onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                                placeholder="e.g., Base Price, Total Price, Net Value"
                              />
                            </div>
                          </div>

                          {/* Row 2: From Step, To Step, Account Key */}
                          <div className="grid grid-cols-6 gap-3">
                            <div>
                              <Label className="text-xs">From Step</Label>
                              <Input
                                type="number"
                                min="1"
                                value={step.from_step || ''}
                                onChange={(e) => handleStepChange(index, 'from_step', e.target.value ? Math.max(1, parseInt(e.target.value)) : null)}
                                placeholder="-"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">To Step</Label>
                              <Input
                                type="number"
                                min="1"
                                value={step.to_step || ''}
                                onChange={(e) => handleStepChange(index, 'to_step', e.target.value ? Math.max(1, parseInt(e.target.value)) : null)}
                                placeholder="-"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Account Key</Label>
                              <Input
                                value={step.account_key || ''}
                                onChange={(e) => handleStepChange(index, 'account_key', e.target.value.toUpperCase())}
                                placeholder="e.g., ERL"
                                maxLength={3}
                                className="uppercase"
                              />
                            </div>
                            <div className="col-span-3 flex items-end gap-4">
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={step.is_statistical}
                                  onChange={(e) => handleStepChange(index, 'is_statistical', e.target.checked)}
                                  className="rounded border-gray-300 w-3 h-3"
                                />
                                Statistical
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        <Button type="button" size="sm" variant="ghost" className="text-red-600 mt-4" onClick={() => handleRemoveStep(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-gray-500 mt-3">
                  Steps are calculated in order (sorted by Step #). Condition Type is optional for subtotal/total lines.
                  "From/To Step" defines calculation range. "Account Key" assigns G/L accounts (e.g., ERL=Revenue, ERS=Sales Deductions).
                </p>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving} data-testid="submit-pricing-procedure">
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingProcedure ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingProcedure ? 'Update Pricing Procedure' : 'Create Pricing Procedure'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default PricingProcedures;
